import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { CONFIG_MAPA_POR_DEFECTO, type ConfigMapa } from "../grafo/grafoSegunConfig";
import { mismaConfigMapa } from "../datos/configMapa";

/**
 * El panel de Configuración del mapa: dos interruptores que cambian la FORMA
 * del circuito (ADR-2026-08-17-mapa-configuracion-y-grafo-derivado).
 *
 * Va superpuesto en la esquina de abajo a la izquierda del lienzo, que es la
 * zona grande que el grafo deja libre (ver `.mapa-config` en index.css).
 * Colapsado es un chip: la pantalla es un diagrama que tiene que entrar entero
 * y el panel abierto tapa parte del mapa, así que el estado de reposo es el
 * que no compite con el dibujo.
 *
 * Este componente NO deriva el grafo ni sabe qué nodos cambia cada opción:
 * emite una `ConfigMapa` y listo. La derivación es una función pura aparte
 * (`grafo/grafoSegunConfig.ts`) y la persistencia otra (`datos/configMapa.ts`).
 *
 * SE OPERA ENTERO CON EL TECLADO, ida y vuelta. Abrir y cerrar cambia de rama
 * del ternario y desmonta el elemento enfocado, así que el foco se mueve a
 * mano en cada transición (ver `useEffect` más abajo): sin eso el foco caía a
 * `<body>`, el `Escape` no llegaba nunca al manejador y volver al chip
 * obligaba a tabular la página entera, nodos del SVG incluidos.
 *
 * LOS DOS TEXTOS DEL INTERRUPTOR SE VEN SIEMPRE. Un switch de iOS obliga a
 * saber de antemano qué significa "prendido", y acá las dos posiciones son
 * modos de trabajo legítimos —no "bien" y "mal"—: con la generación apagada el
 * sistema sigue haciendo algo (entrega el plan). Por eso un segmentado con
 * "Activada" y "Desactivada" a la vista y una pastilla que se desliza al lado
 * elegido, y por eso la descripción de abajo CAMBIA con el estado: dice lo que
 * el sistema hace con ese valor puesto, no lo que la opción significa.
 */

interface OpcionConfig {
  campo: keyof ConfigMapa;
  titulo: string;
  activada: string;
  desactivada: string;
}

const OPCIONES: OpcionConfig[] = [
  {
    campo: "generacionDeContenido",
    titulo: "Generación de contenido",
    activada: "El sistema genera con IA el contenido de forma automática.",
    desactivada: "El sistema define qué contenido hace falta y un plan de acción para implementarlo.",
  },
  {
    campo: "publicacionAutomatica",
    titulo: "Publicación automática",
    activada: "Publica la campaña automáticamente, sin pasar por vos.",
    desactivada: "Espera tu confirmación antes de publicar.",
  },
];

/**
 * El segmentado. La pastilla es UNA sola que se desliza —no dos fondos que se
 * prenden y apagan— así que el ojo sigue el cambio en vez de tener que
 * compararlo. Ancho y desplazamiento en % del riel (nunca en px), para que no
 * dependan del largo de las palabras.
 *
 * Radios concéntricos: panel r=16 con 8 de padding → riel r=8; riel con 2 de
 * padding → pastilla r=6.
 *
 * EL EJE LO DECIDE EL CSS, no este componente: con el panel angosto las dos
 * opciones se APILAN (una arriba de la otra) porque "Desactivada" no entra en
 * media columna, y ahí la pastilla se desliza en vertical. Por eso el estado
 * viaja como una variable CSS con el desplazamiento en % (`--seg-pct`) y el
 * `translateX`/`translateY` sale de la hoja de estilos, donde vive la consulta
 * de contenedor (`.mapa-seg` en index.css). En un style inline el eje quedaría
 * fijado desde acá y le ganaría en especificidad a la consulta.
 */
const Interruptor = ({
  valor,
  etiqueta,
  onCambiar,
}: {
  valor: boolean;
  etiqueta: string;
  onCambiar: (valor: boolean) => void;
}) => (
  <div
    role="group"
    aria-label={etiqueta}
    className="mapa-seg relative mt-1 grid rounded-[8px] bg-[#F0F2F7] p-[2px]"
    style={{ "--seg-pct": valor ? "0%" : "100%" } as CSSProperties}
  >
    <span
      aria-hidden="true"
      className="mapa-seg-pastilla absolute left-[2px] top-[2px] rounded-[6px] bg-white shadow-[0_1px_2px_rgba(2,6,43,0.14)] transition-transform duration-200 ease-out"
    />
    {[true, false].map((opcion) => (
      <button
        key={String(opcion)}
        type="button"
        aria-pressed={valor === opcion}
        onClick={() => onCambiar(opcion)}
        className={`relative z-10 rounded-[6px] py-1 px-2 text-center transition-colors ${
          valor === opcion ? "font-semibold text-brand-primary" : "text-[#02062B]/[0.55]"
        }`}
      >
        {opcion ? "Activada" : "Desactivada"}
      </button>
    ))}
  </div>
);

export const PanelConfigMapa = ({
  config,
  onCambiar,
}: {
  config: ConfigMapa;
  onCambiar: (config: ConfigMapa) => void;
}) => {
  const [abierto, setAbierto] = useState(false);
  const enDefecto = mismaConfigMapa(config, CONFIG_MAPA_POR_DEFECTO);

  // EL FOCO SE MUEVE A MANO EN CADA TRANSICIÓN. El chip y el panel son las dos
  // ramas de un ternario: al abrir, React DESMONTA el botón que tenía el foco
  // y el foco cae a `<body>`. Con eso el `Escape` de acá abajo quedaba muerto
  // —está en un `<div>` sin `tabIndex`, así que sólo lo alcanzan los eventos
  // que burbujean desde un descendiente enfocado— y el chip quedaba
  // inalcanzable al cerrar. Así que al abrir el foco va al botón que cierra
  // (primer control del panel) y al cerrar vuelve al chip.
  const chipRef = useRef<HTMLButtonElement>(null);
  const cerrarRef = useRef<HTMLButtonElement>(null);
  // Sin esto el panel se robaría el foco al montar la pantalla: sólo se mueve
  // el foco cuando el que abrió o cerró fue el usuario.
  const abrioElUsuario = useRef(false);

  useEffect(() => {
    if (!abrioElUsuario.current) return;
    (abierto ? cerrarRef.current : chipRef.current)?.focus();
  }, [abierto]);

  const cambiarAbierto = (nuevo: boolean) => {
    abrioElUsuario.current = true;
    setAbierto(nuevo);
  };

  return (
    <div
      className="mapa-config absolute bottom-0 left-0 flex max-h-full flex-col items-start justify-end"
      onKeyDown={(ev) => {
        if (ev.key === "Escape") cambiarAbierto(false);
      }}
    >
      {abierto ? (
        <div className="mapa-config-panel rounded-[16px] border border-[#02062B]/[0.08] bg-white p-[8px] shadow-[0_8px_24px_rgba(2,6,43,0.12)]">
          <button
            type="button"
            ref={cerrarRef}
            onClick={() => cambiarAbierto(false)}
            aria-expanded={true}
            className="flex w-full items-center justify-between gap-2 text-left font-semibold text-brand-black"
          >
            <span className="flex items-center gap-1.5">
              <SlidersHorizontal className="mapa-config-icono shrink-0" aria-hidden="true" />
              Configuración
            </span>
            <ChevronDown className="mapa-config-icono shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
          </button>

          {OPCIONES.map((opcion) => (
            <div key={opcion.campo} className="mt-2.5">
              <p className="font-medium text-brand-black">{opcion.titulo}</p>
              <Interruptor
                valor={config[opcion.campo]}
                etiqueta={opcion.titulo}
                onCambiar={(valor) => onCambiar({ ...config, [opcion.campo]: valor })}
              />
              {/* `aria-live`: el que acaba de tocar el interruptor con lector
                  de pantalla no ve deslizarse la pastilla — lo que cambia para
                  él es este texto. */}
              <p className="mapa-config-desc mt-1 text-[#02062B]/[0.55]" aria-live="polite">
                {config[opcion.campo] ? opcion.activada : opcion.desactivada}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <button
          type="button"
          ref={chipRef}
          onClick={() => cambiarAbierto(true)}
          aria-expanded={false}
          // Cuerpo de letra y padding (2:1) van en `.mapa-config-chip`, en `em`,
          // para que el chip crezca de una pieza con el panel — con `py-*`/`px-*`
          // de Tailwind el aire quedaba en px fijos y el chip se veía chico
          // (ver `.mapa-config-chip` en index.css).
          className="mapa-config-chip inline-flex items-center gap-1.5 rounded-full border border-[#02062B]/[0.08] bg-white font-medium text-brand-black shadow-[0_2px_8px_rgba(2,6,43,0.10)]"
        >
          <SlidersHorizontal className="mapa-config-icono shrink-0" aria-hidden="true" />
          Configuración
          {/* Con el panel cerrado, el circuito puede estar dibujando algo
              distinto de lo que hace el producto por defecto y no habría con
              qué saberlo. El punto es esa señal.

              El punto es DECORATIVO (`aria-hidden`) y la señal viaja en el
              NOMBRE del botón, con un texto sólo para lectores. Un `aria-label`
              sobre el `<span>` no servía: es un elemento de rol `generic`, y
              ARIA prohíbe nombrarlos — Chromium y Firefox lo descartan también
              al calcular el nombre del botón que lo contiene, así que la única
              señal de "esto no es lo de por defecto" no llegaba. */}
          {!enDefecto && (
            <>
              <span
                className="mapa-config-punto shrink-0 rounded-full bg-brand-primary"
                aria-hidden="true"
              />
              <span className="sr-only">, distinta de la de por defecto</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default PanelConfigMapa;
